import {
  Fragment, type ReactNode
} from "react";
import {
  Dialog, Transition
} from "@headlessui/react";
import {
  XMarkIcon
} from "@heroicons/react/24/outline";

interface ChannelModalShellProps {
  isOpen: boolean;
  /** HeadlessUI Dialog ``onClose`` (backdrop/escape). All three channels pass
   *  a no-op today: a backdrop click during pairing reads as an accident, and
   *  the header X / footer Cancel remain the deliberate way out. */
  dialogOnClose: () => void;
  /** Invoked by the header X button and the footer Cancel button. */
  onClose: () => void;
  /** Tailwind background class for the icon circle (e.g. "bg-gray-100"). */
  iconWrapperClass: string;
  /** The channel glyph rendered inside the icon circle. */
  icon: ReactNode;
  title: ReactNode;
  /** Show the centered spinner + ``loadingLabel`` (covers loading/verifying). */
  loading: boolean;
  loadingLabel: ReactNode;
  /** Tailwind border-color for the spinner (e.g. "border-blue-600"). */
  spinnerColorClass: string;
  error?: string | null;
  /** Optional action rendered inside the error box (WhatsApp's "Try again"). */
  errorAction?: ReactNode;
  children: ReactNode;
}

/** The Messages bubble both iMessage pairing modals put in the header. */
export const IMESSAGE_ICON = (
  <svg className="h-6 w-6 text-black" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
  </svg>
);

/**
 * Shared chrome for the channel-pairing modals (SMS / iMessage / WhatsApp):
 * the gray-overlay HeadlessUI dialog, the icon + title header, the
 * loading-spinner and error states, and the footer Cancel button. The
 * step-specific body is supplied as ``children``.
 */
export function ChannelModalShell({
  isOpen,
  dialogOnClose,
  onClose,
  iconWrapperClass,
  icon,
  title,
  loading,
  loadingLabel,
  spinnerColorClass,
  error,
  errorAction,
  children,
}: ChannelModalShellProps) {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={dialogOnClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-y-auto max-h-[90vh] rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-[opacity,transform] sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconWrapperClass} sm:mx-0 sm:h-10 sm:w-10`}>
                    {icon}
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      {title}
                    </Dialog.Title>

                    {loading && (
                      <div className="mt-4">
                        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${spinnerColorClass} mx-auto`}></div>
                        <p className="text-sm text-gray-500 mt-2">
                          {loadingLabel}
                        </p>
                      </div>
                    )}

                    {error && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-sm text-red-800">{error}</p>
                        {errorAction}
                      </div>
                    )}

                    {children}
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
